import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { useAuthGate } from '../../Context/AuthGateContext';
import { useToast } from '../../Context/ToastContext';
import { apiFetch } from '../../services/apiClient';
import { io } from 'socket.io-client';
import { FiSun, FiMoon } from 'react-icons/fi';
import { FaBitcoin } from 'react-icons/fa';
import NotificationBell from '../NotificationsComponents/NotificationBell';
import NotificationPanel from '../NotificationsComponents/NotificationPanel';
import WalletIdentityChip from './WalletIdentityChip';
import './Header.css';

const API_URL    = process.env.REACT_APP_API_URL    || 'http://localhost:4000';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || API_URL;

export default function Header({ toggleMenu }) {
  const navigate         = useNavigate();
  const { user, updateUser } = useAuth();
  const { openAuth }     = useAuthGate();
  const { showToast }    = useToast();

  const [open, setOpen]                   = useState(false);
  const [btcPrice, setBtcPrice]           = useState(null);
  const [priceUp, setPriceUp]             = useState(null);
  const [priceFlash, setPriceFlash]       = useState(false);
  const [backendOk, setBackendOk]         = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [hasMore, setHasMore]             = useState(false);
  const [loadingMore, setLoadingMore]     = useState(false);
  const [scrolled, setScrolled]           = useState(false);

  const wrapperRef = useRef(null);
  const prevPrice  = useRef(null);
  // Preferencias de notificación del usuario (default = todo activado).
  const prefs      = user?.notifyPrefs || {};
  const prefsRef   = useRef(prefs);
  prefsRef.current = prefs;

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('bitplify_theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('bitplify_theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Sync when MobileNav changes theme
  useEffect(() => {
    const handler = () => {
      const saved = localStorage.getItem('bitplify_theme');
      setDarkMode(saved === 'dark');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Scroll detection for compact header
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleOutsideClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // BTC price
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const res  = await fetch('https://data-api.binance.vision/api/v3/ticker/price?symbol=BTCUSDT');
        const data = await res.json();
        const newPrice = parseFloat(data.price);
        if (prevPrice.current !== null) {
          const up = newPrice >= prevPrice.current;
          setPriceUp(up);
          // Flash animation on price change
          if (newPrice !== prevPrice.current) {
            setPriceFlash(true);
            setTimeout(() => setPriceFlash(false), 600);
          }
        }
        prevPrice.current = newPrice;
        setBtcPrice(newPrice);
      } catch { setBtcPrice(null); }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 15000);
    return () => clearInterval(interval);
  }, []);

  // Backend status
  useEffect(() => {
    const checkBackend = async () => {
      try {
        const res = await fetch(`${API_URL}/api/health`, {
          signal: AbortSignal.timeout(3000),
        });
        setBackendOk(res.ok);
      } catch { setBackendOk(false); }
    };
    checkBackend();
    const interval = setInterval(checkBackend, 30000);
    return () => clearInterval(interval);
  }, []);

  // Notifications — carga inicial + respaldo por polling. 'merge' solo agrega lo
  // nuevo para no pisar la lista expandida ("Ver más") ni los estados de leído.
  const fetchNotifications = async (mode = 'replace') => {
    if (!localStorage.getItem('token')) return;
    try {
      const res = await apiFetch('/api/notifications');
      if (!res.ok) return;
      const data = await res.json();
      if (mode === 'replace') {
        setNotifications(data);
        setHasMore(data.length >= 20);
      } else {
        setNotifications(prev => {
          const known = new Set(prev.map(n => n.id));
          const fresh = data.filter(n => !known.has(n.id));
          return fresh.length ? [...fresh, ...prev] : prev;
        });
      }
    } catch {}
  };

  useEffect(() => {
    fetchNotifications('replace');
    const interval = setInterval(() => fetchNotifications('merge'), 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Notificaciones en tiempo real (campana + toast). El backend une el socket a
  // la sala user_<id> según el JWT y emite 'new_notification'. El polling de
  // arriba queda como respaldo si el socket se cae.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!user?.id || !token) return;
    const socket = io(SOCKET_URL, { transports: ['websocket'], auth: { token } });
    socket.on('new_notification', (n) => {
      setNotifications(prev => (prev.some(x => x.id === n.id) ? prev : [n, ...prev]));
      // El toast respeta las preferencias por tipo; la campana siempre lo registra.
      if (prefsRef.current[n.type] !== false) {
        showToast({
          type:    n.type,
          message: n.message,
          onClick: n.tradeId ? () => navigate(`/trades/${n.tradeId}`) : undefined,
        });
      }
    });
    return () => socket.disconnect();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paginación: trae las anteriores a la más vieja cargada (cursor por id).
  const loadMore = async () => {
    if (loadingMore || notifications.length === 0) return;
    setLoadingMore(true);
    try {
      const oldest = notifications[notifications.length - 1].id;
      const res = await apiFetch(`/api/notifications?before=${oldest}&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setNotifications(prev => {
          const known = new Set(prev.map(n => n.id));
          return [...prev, ...data.filter(n => !known.has(n.id))];
        });
        setHasMore(data.length >= 20);
      }
    } catch {}
    setLoadingMore(false);
  };

  const markRead = (id) => {
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
    apiFetch(`/api/notifications/${id}/read`, { method: 'PATCH' }).catch(() => {});
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    apiFetch('/api/notifications/read', { method: 'PATCH' }).catch(() => {});
  };

  const togglePref = (key) => {
    const next = { ...prefs, [key]: prefs[key] === false ? true : false };
    updateUser({ notifyPrefs: next });
    apiFetch('/api/me/notification-prefs', {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ [key]: next[key] }),
    }).catch(() => {});
  };

  const handleOpenPanel = () => setOpen(prev => !prev);

  const handleNotificationClick = (notification) => {
    if (notification.id) markRead(notification.id);
    setOpen(false);
    if (notification.tradeId) navigate(`/trades/${notification.tradeId}`);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const formatPrice = (price) => {
    if (!price) return '—';
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formattedNotifications = notifications.map(n => ({
    id:      n.id,
    message: n.message,
    type:    n.type,
    time:    new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    read:    n.read,
    tradeId: n.tradeId ?? null,
  }));

  return (
    <header className={`hdr ${scrolled ? 'hdr-compact' : ''}`}>
      {/* Gradient bottom line */}
      <div className="hdr-gradient-line" />

      <div className="hdr-inner">
        {/* Left section */}
        <div className="hdr-left">
          <img
            src={darkMode ? '/assets/Logo-White.png' : '/assets/Logo.png'}
            alt="Bitplify"
            className="hdr-logo"
            onClick={() => navigate('/')}
          />

          {/* Separator */}
          <div className="hdr-sep" />

          {/* BTC Ticker — always visible */}
          <div className={`hdr-ticker ${priceUp === true ? 'tick-up' : priceUp === false ? 'tick-down' : ''} ${priceFlash ? 'tick-flash' : ''}`}>
            <span className="tick-label fin-label">BTC</span>
            <span className="tick-icon"><FaBitcoin /></span>
            <span className="tick-price fin-num">{formatPrice(btcPrice)}</span>
            {priceUp !== null && (
              <span className="tick-arrow fin-num">{priceUp ? '▲' : '▼'}</span>
            )}
          </div>
        </div>

        {/* Right section */}
        <div className="hdr-right">
          {/* Backend status — minimal dot */}
          <div
            className={`hdr-status ${backendOk === null ? 'st-check' : backendOk ? 'st-on' : 'st-off'}`}
            title={backendOk === null ? 'Conectando...' : backendOk ? 'Backend online' : 'Backend offline'}
          >
            <span className="st-dot" />
            <span className="st-text">
              {backendOk === null ? 'Conectando' : backendOk ? 'Online' : 'Offline'}
            </span>
          </div>

          {/* Dark mode toggle — desktop */}
          <button
            className="hdr-theme-btn desktop-only"
            onClick={() => setDarkMode(prev => !prev)}
            title={darkMode ? 'Modo claro' : 'Modo oscuro'}
          >
            <span className={`theme-icon ${darkMode ? 'theme-dark' : 'theme-light'}`}>
              {darkMode ? <FiSun /> : <FiMoon />}
            </span>
          </button>

          {/* Notificaciones (logueado) o CTA de invitado */}
          {user ? (
            <>
            {user.walletAddress && (
              <WalletIdentityChip address={user.walletAddress} onClick={() => navigate('/profile')} />
            )}
            <div className="notification-wrapper" ref={wrapperRef}>
              <NotificationBell count={unreadCount} onClick={handleOpenPanel} />
              {open && (
                <NotificationPanel
                  notifications={formattedNotifications}
                  onClose={() => setOpen(false)}
                  onNotificationClick={handleNotificationClick}
                  onMarkAll={markAllRead}
                  onLoadMore={loadMore}
                  hasMore={hasMore}
                  loadingMore={loadingMore}
                  prefs={prefs}
                  onTogglePref={togglePref}
                />
              )}
            </div>
            </>
          ) : (
            <div className="hdr-guest">
              <button className="hdr-guest-btn ghost" onClick={() => openAuth('login')}>Entrar</button>
              <button className="hdr-guest-btn solid" onClick={() => openAuth('register')}>Registrarse</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
